import { acquireGraphTokenSilent } from '@/services/auth/msalClient';

const GRAPH = 'https://graph.microsoft.com/v1.0';

export interface GraphMe {
  /** "Prénom NOM" (surname upper-cased, French convention), when derivable. */
  fullName?: string;
  /** The user's profile photo as a data URL, ready for <Avatar image>. */
  photoDataUrl?: string;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

// Fetches the signed-in user's name and photo from Microsoft Graph. Entirely
// best-effort: without a Graph token (or on any Graph error) it resolves to an
// empty object and the avatar falls back to initials + the Office display name.
export async function fetchGraphMe(): Promise<GraphMe> {
  const token = await acquireGraphTokenSilent();
  if (!token) return {};
  const headers = { Authorization: `Bearer ${token}` };
  const out: GraphMe = {};

  try {
    const res = await fetch(`${GRAPH}/me?$select=givenName,surname,displayName`, { headers });
    if (res.ok) {
      const u = (await res.json()) as {
        givenName?: string | null;
        surname?: string | null;
        displayName?: string | null;
      };
      const given = (u.givenName ?? '').trim();
      const surname = (u.surname ?? '').trim();
      if (given && surname) out.fullName = `${given} ${surname.toUpperCase()}`;
      else out.fullName = (u.displayName ?? '').trim() || undefined;
    }
  } catch {
    /* name enrichment is best-effort */
  }

  try {
    // 96x96 covers the 40px popover avatar at 2x. Some mailboxes 404 on a
    // specific size, so fall back to the default photo; a user with no photo
    // at all 404s both and we simply skip it.
    let res = await fetch(`${GRAPH}/me/photos/96x96/$value`, { headers });
    if (!res.ok) res = await fetch(`${GRAPH}/me/photo/$value`, { headers });
    if (res.ok) out.photoDataUrl = await blobToDataUrl(await res.blob());
  } catch {
    /* photo enrichment is best-effort */
  }

  return out;
}
